import React, { useRef, useState } from 'react';
import { Upload, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

interface ImageUploaderProps {
  value?: string;
  onChange?: (url: string) => void;
  currentImage?: string; // Alternative prop name
  onImageChange?: (url: string) => void; // Alternative prop name
  label?: string;
  recommendation?: string; // e.g., "1920x600px • Max 2MB"
  aspectRatio?: string; // e.g., "16:9", "1:1", "21:9", "avatar", "icon"
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ value, onChange, currentImage, onImageChange, label, recommendation, aspectRatio }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Support both prop naming conventions
  const imageValue = value || currentImage || '';
  const handleImageChange = onChange || onImageChange || (() => {});
  
  // Auto-generate recommendation based on aspect ratio if not provided
  // 📱 MOBILE FIRST - ขนาดเหมาะกับมือถือเป็นหลัก (based on actual render size)
  const getDefaultRecommendation = () => {
    switch (aspectRatio) {
      case '21:9': return '780x560px • Mobile Banner (7:5) • Max 400KB';
      case '16:9': return '800x450px • Video/Hero • Max 500KB';
      case '4:3': return '780x585px • Standard (4:3) • Max 400KB';
      case '3:2': return '390x260px • Carousel (3:2) • Max 300KB';
      case '2:1': return '160x96px • Progress Card (5:3) • Max 100KB';
      case '1:1': return '400x400px • Square • Max 300KB';
      case 'avatar': return '200x200px • Profile • Max 100KB';
      case 'icon': return '128x128px • Icon • Max 50KB';
      default: return '800x600px • Mobile first • Max 500KB';
    }
  };
  
  const displayRecommendation = recommendation || getDefaultRecommendation();

  // Get aspect ratio class for proper image display
  // 📱 ใช้ aspect ratio ที่ตรงกับ mobile rendering จริงๆ
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '21:9': return 'aspect-[7/5]'; // Banner mobile (390×280 = 7:5)
      case '16:9': return 'aspect-video';
      case '4:3': return 'aspect-[4/3]';
      case '3:2': return 'aspect-[3/2]'; // Carousel (390×260 = 3:2)
      case '2:1': return 'aspect-[5/3]'; // GameRTP card (160×96 = 5:3)
      case '1:1': return 'aspect-square';
      case 'avatar': return 'aspect-square';
      case 'icon': return 'aspect-square';
      default: return 'aspect-[4/3]'; // Default to 4:3
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();
      handleImageChange(data.url);
      toast.success('Image uploaded successfully!');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    handleImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium mb-2 text-gray-300">{label}</label>}
      
      {imageValue ? (
        <div className="relative group">
          <img
            src={imageValue}
            alt="Uploaded"
            className={`w-full ${getAspectRatioClass()} object-cover rounded-lg border border-gray-700`}
          />
          <button
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X size={16} className="text-white" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`w-full ${getAspectRatioClass()} border-2 border-dashed border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-gray-800/50 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isUploading ? (
            <>
              <Loader size={32} className="text-blue-500 mb-2 animate-spin" />
              <span className="text-sm text-blue-400">Uploading...</span>
            </>
          ) : (
            <>
              <Upload size={32} className="text-gray-500 mb-2" />
              <span className="text-sm text-gray-400">Click to upload image</span>
              <span className="text-xs text-gray-500 mt-1">💡 {displayRecommendation}</span>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {imageValue && (
        <div className="mt-2">
          <label className="block text-xs text-gray-400 mb-1">Image URL:</label>
          <input
            type="text"
            value={imageValue}
            onChange={(e) => handleImageChange(e.target.value)}
            placeholder="Or paste image URL"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            title={imageValue}
          />
          {imageValue.includes('supabase.co') && (
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              ✅ Uploaded to Supabase Storage
            </p>
          )}
        </div>
      )}
    </div>
  );
};

